import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "./Reveal";

export default function Hero({ hero }) {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const onMove = (e) => {
      if (!imgRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 18;
      const y = (e.clientY / window.innerHeight - 0.5) * 18;
      imgRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const lines = hero?.title_lines?.length ? hero.title_lines : ["TIMBALAND", "DA KING"];

  return (
    <section id="top" ref={ref} className="relative h-[100svh] w-full overflow-hidden vignette" data-testid="hero-section">
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0">
        <div ref={imgRef} className="absolute inset-[-4%] transition-transform duration-300 ease-out">
          {hero?.background_image && (
          <img
            src={hero.background_image}
            alt="Studio"
            className="w-full h-full object-cover object-center opacity-80"
          />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/70" />
        <div
          className="absolute inset-0 mix-blend-soft-light pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(67,132,64,0.55), rgba(79,176,74,0.18) 45%, transparent 78%)" }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 15% 85%, rgba(79,176,74,0.18), transparent 55%)" }}
        />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full max-w-[1600px] mx-auto flex flex-col justify-end items-center md:items-start text-center md:text-left pb-16 md:pb-24 px-6 md:px-12"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-7"
          data-testid="hero-meta"
        >
          <span className="font-display uppercase text-[0.7rem] tracking-[0.28em] text-gold">Virginia Native</span>
          <span className="w-1.5 h-1.5 rotate-45 bg-white/40" />
          <span className="font-display uppercase text-[0.7rem] tracking-[0.28em] text-white/70">Grammy-Winning Producer</span>
          <span className="w-1.5 h-1.5 rotate-45 bg-white/40" />
          <span className="font-display uppercase text-[0.7rem] tracking-[0.28em] text-white/70">King of Music Innovation</span>
        </motion.div>

        {hero?.logo_image ? (
          <motion.img
            src={hero.logo_image}
            alt={lines.join(" ")}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-[300px] sm:max-w-[520px] lg:max-w-[760px] h-auto object-contain"
            data-testid="hero-logo"
          />
        ) : (
          <h1 className="headline text-[19vw] md:text-[13vw] lg:text-[12rem] text-white">
            {lines.map((line, i) => (
              <MaskLine key={i} delay={0.35 + i * 0.15}>
                {lines.length > 1 && i === lines.length - 1 ? (
                  <span className="text-gold">{line}</span>
                ) : (
                  line
                )}
              </MaskLine>
            ))}
          </h1>
        )}

        <div className="flex flex-col items-center md:flex-row md:items-end md:justify-between gap-8 mt-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="max-w-md text-white/70 text-base md:text-lg leading-relaxed font-body mx-auto md:mx-0"
          >
            {hero?.subtitle}
          </motion.p>
          <motion.a
            href={hero?.cta_target || "#biography"}
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 1 }}
            className="group inline-flex items-center gap-4 font-display uppercase tracking-[0.2em] text-sm text-white border border-white/25 px-8 py-4 hover:bg-gold hover:text-black hover:border-gold transition-colors duration-300 w-fit"
          >
            {hero?.cta_label || "Enter The Archive"}
            <span className="group-hover:translate-x-1 transition-transform">↓</span>
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
}
